import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { createWorkout, createWorkoutsBatch, updateWorkout, deleteWorkout, getTrainingPrograms, getExercises, getTrainerLocations, createTrainerLocation, updateTrainerLocation, deleteTrainerLocation } from '../services/api';
import {
  FullScreenModal,
  FullScreenModalContent,
  FullScreenModalHeader,
  FullScreenModalTitle,
  FullScreenModalBackButton,
  FullScreenModalBody,
} from './ui/fullscreen-modal';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent } from './ui/card';
import ClientSelectorField from './ClientSelectorField';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  AlertCircle, 
  CheckCircle,
  Users,
  Calendar,
  Clock,
  MapPin,
  Repeat,
  FileText,
  Trash2,
  Plus,
  X,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { cn } from '../lib/utils';
import { format, parse } from 'date-fns';
import { useI18n } from '../i18n/context';
import { getDateFnsLocale, getIntlLocaleTag } from '../lib/localeRuntime';
import { API_LOCATION_ONLINE_LABEL } from '../i18n/workoutConstants';

/** Human-readable batch conflict slot from POST /workouts/batch */
function describeBatchTimeConflict(c, t, locale) {
  if (!c?.date) return t('optForm.batchUnknownDate');
  try {
    const d = parse(c.date, 'yyyy-MM-dd', new Date());
    const loc = getDateFnsLocale(locale);
    const dayAndDate = format(d, 'EEEE, d MMMM yyyy', { locale: loc });
    const timeStr = (c.time || '').toString().slice(0, 5);
    const clients = Array.isArray(c.conflictingClients) ? c.conflictingClients.filter(Boolean).join(', ') : '';
    let line = `${dayAndDate} — ${timeStr}`;
    if (clients) {
      line += ` (${t('optForm.batchBusy', { clients })})`;
    }
    return line;
  } catch {
    return `${c.date} ${(c.time || '').toString().slice(0, 5)}`;
  }
}

const OptimizedWorkoutForm = ({ show, onHide, onSave, workout = null, trainerId = null, initialDate = null }) => {
  const { t, locale } = useI18n();
  const dateLocale = getDateFnsLocale(locale);

  const durationOptions = useMemo(
    () => [
      { value: 30, label: t('optForm.duration30') },
      { value: 45, label: t('optForm.duration45') },
      { value: 60, label: t('optForm.duration60') },
      { value: 90, label: t('optForm.duration90') },
      { value: 120, label: t('optForm.duration120') },
      { value: 'custom', label: t('optForm.durationCustom') },
    ],
    [t]
  );

  const weekdayShortLabels = useMemo(() => {
    const base = new Date(2023, 0, 1);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      return format(d, 'EEE', { locale: dateLocale });
    });
  }, [dateLocale]);
  const isEditMode = !!workout;
  const [hasChanges, setHasChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    clients: [],
    startDate: null,
    startTime: null,
    duration: 60,
    endTime: null, // Для "Другое" в продолжительности
    location: '', // Будет хранить 'online' или location_id
    locationType: null, // 'online' или 'offline'
    recurrenceType: 'none',
    recurrenceSchedule: [], // Массив { dayOfWeek: 0-6, time: 'HH:MM' }
    recurrenceEndType: 'weeks', // 'weeks' | 'date'
    recurrenceWeeks: '', // число недель или '' пока не ввели
    recurrenceEndDate: null, // Дата окончания
    customRecurrenceSettings: {
      frequency: 'daily',
      interval: 1,
      intervalType: 'days',
      daysOfWeek: [],
      endType: 'never',
      endCount: 1,
      endDate: null
    },
    programId: null,
    exercises: [],
    comment: ''
  });

  const [trainingPrograms, setTrainingPrograms] = useState([]);
  const [trainerLocations, setTrainerLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [scheduleConflictError, setScheduleConflictError] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showTimeConflictDialog, setShowTimeConflictDialog] = useState(false);
  const [timeConflictDetails, setTimeConflictDetails] = useState(null); // { conflicts?: any[], source?: 'batch' | 'single' }
  const [pendingTimeConflictWorkoutData, setPendingTimeConflictWorkoutData] = useState(null);
  const [pendingBatchWorkouts, setPendingBatchWorkouts] = useState(null);
  /** В диалоге конфликта серии: показать все слоты или только первые 3 */
  const [batchConflictSlotsExpanded, setBatchConflictSlotsExpanded] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showProgramSelector, setShowProgramSelector] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [workoutPlanId, setWorkoutPlanId] = useState(null);
  const [showAddLocationForm, setShowAddLocationForm] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationAddress, setNewLocationAddress] = useState('');
  const [isAddingLocation, setIsAddingLocation] = useState(false);

  /** После открытия формы создания — один раз подставить зал «по умолчанию» из профиля */
  const shouldApplyDefaultLocationRef = useRef(false);

  /** Если true — комментарий копируется во все тренировки серии; если false — только к первой */
  const [applyCommentToAllRecurring, setApplyCommentToAllRecurring] = useState(false);

  // Загрузка данных
  useEffect(() => {
    if (show) {
      if (trainerId) {
        loadTrainingPrograms();
        loadTrainerLocations();
      }
      if (workout) {
        loadWorkoutData();
        loadWorkoutExercises();
      } else {
        // Используем initialDate если он передан, иначе текущее время
        const now = initialDate ? new Date(initialDate) : new Date();
        const startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0); // Только дата, без времени
        
        const startTime = new Date(now);
        startTime.setMinutes(0);
        startTime.setSeconds(0);
        
        setFormData({
          clients: [],
          startDate: startDate,
          startTime: startTime,
          duration: 60,
          endTime: null,
          location: '', // Будет хранить 'online' или location_id
    locationType: null, // 'online' или 'offline'
          recurrenceType: 'none',
          recurrenceSchedule: [],
          recurrenceEndType: 'weeks',
          recurrenceWeeks: '',
          recurrenceEndDate: null,
          customRecurrenceSettings: {
            frequency: 'daily',
            interval: 1,
            intervalType: 'days',
            daysOfWeek: [],
            endType: 'never',
            endCount: 1,
            endDate: null
          },
          programId: null,
          exercises: [],
          comment: '',
          planId: null
        });
        setSelectedPlanId(null);
        setHasChanges(false);
        setError('');
        setScheduleConflictError('');
        setValidationErrors({});
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }
    }
  }, [show, workout, trainerId, initialDate]);

  useEffect(() => {
    if (show && !workout) {
      shouldApplyDefaultLocationRef.current = true;
    } else if (!show) {
      shouldApplyDefaultLocationRef.current = false;
    }
  }, [show, workout]);

  useEffect(() => {
    if (!show || workout || !trainerLocations.length || !shouldApplyDefaultLocationRef.current) return;
    const def = trainerLocations.find(
      (l) => l.is_default === true || l.is_default === 1 || l.is_default === 'true'
    );
    if (!def?.id) {
      shouldApplyDefaultLocationRef.current = false;
      return;
    }
    setFormData((prev) => {
      if (prev.location || prev.locationType) {
        shouldApplyDefaultLocationRef.current = false;
        return prev;
      }
      shouldApplyDefaultLocationRef.current = false;
      return { ...prev, location: def.id, locationType: 'offline' };
    });
  }, [trainerLocations, show, workout]);

  const isRecurringSeries = Array.isArray(formData.recurrenceSchedule) && formData.recurrenceSchedule.length > 0;

  useEffect(() => {
    if (!isRecurringSeries) {
      setApplyCommentToAllRecurring(false);
    }
  }, [isRecurringSeries]);

  const loadTrainingPrograms = async () => {
    try {
      const response = await getTrainingPrograms(trainerId);
      // Ensure we always have an array
      let programs = [];
      if (response && response.data) {
        programs = Array.isArray(response.data) ? response.data : [];
      } else if (Array.isArray(response)) {
        programs = response;
      }
      setTrainingPrograms(programs);
    } catch (err) {
      console.error('Ошибка загрузки программ:', err);
    }
  };

  const loadTrainerLocations = async () => {
    try {
      const response = await getTrainerLocations(trainerId);
      setTrainerLocations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Ошибка загрузки локаций:', err);
      // Set default locations even if loading fails
      setTrainerLocations([]);
    }
  };

  // Function to generate recurring workout dates and times based on schedule
  const generateRecurringWorkouts = (startDate, recurrenceSchedule, endType, weeks, endDate, defaultTime) => {
    const workouts = []; // Массив { date: Date, time: string }
    
    if (!recurrenceSchedule || recurrenceSchedule.length === 0) {
      // Если нет расписания, возвращаем одну тренировку на startDate
      const timeStr = defaultTime ? new Date(defaultTime).toTimeString().slice(0, 5) : '09:00';
      return [{ date: new Date(startDate), time: timeStr }];
    }

    // Определяем количество недель и дату окончания
    let numberOfWeeks;
    let finalDate;
    
    if (endType === 'weeks') {
      numberOfWeeks = parseInt(weeks, 10);
      if (!Number.isFinite(numberOfWeeks) || numberOfWeeks < 1) {
        console.warn('[generateRecurringWorkouts] Invalid weeks count:', weeks);
        return [];
      }

      // Вычисляем дату окончания для валидации
      finalDate = new Date(startDate);
      finalDate.setDate(finalDate.getDate() + (numberOfWeeks * 7));
      finalDate.setHours(23, 59, 59);
    } else if (endType === 'date' && endDate) {
      finalDate = new Date(endDate);
      finalDate.setHours(23, 59, 59);
      // Вычисляем количество недель до даты окончания
      const daysDiff = Math.ceil((finalDate.getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24));
      numberOfWeeks = Math.max(1, Math.ceil(daysDiff / 7));
    } else {
      console.warn('[generateRecurringWorkouts] No valid end: endType=', endType, 'endDate=', endDate);
      return [];
    }

    // Генерируем тренировки для каждой недели (ровно numberOfWeeks недель)
    const startDateObj = new Date(startDate);
    startDateObj.setHours(0, 0, 0, 0);

    // Для каждой недели (0, 1, 2, ..., numberOfWeeks-1)
    for (let week = 0; week < numberOfWeeks; week++) {
      // Для каждого элемента расписания создаем тренировку в этой неделе
      for (const schedule of recurrenceSchedule) {
        // Находим дату нужного дня недели в текущей неделе
        const workoutDate = new Date(startDateObj);
        
        // Вычисляем, сколько дней нужно добавить к startDate
        // Сначала находим день недели startDate
        const startDayOfWeek = startDateObj.getDay();
        const targetDayOfWeek = schedule.dayOfWeek;
        
        // Вычисляем смещение для первой недели
        let daysOffset = targetDayOfWeek - startDayOfWeek;
        if (daysOffset < 0) {
          // Если день уже прошел на этой неделе, берем следующий (через 7 дней)
          daysOffset += 7;
        }
        
        // Добавляем дни для текущей недели
        // Для недели 0: startDate + daysOffset
        // Для недели 1: startDate + daysOffset + 7
        // Для недели 2: startDate + daysOffset + 14
        // и т.д.
        workoutDate.setDate(startDateObj.getDate() + daysOffset + (week * 7));
        
        // Проверяем, что дата не превышает дату окончания (для случая с датой)
        if (workoutDate <= finalDate) {
          workouts.push({
            date: new Date(workoutDate),
            time: schedule.time
          });
        }
      }
    }

    // Сортируем по дате и времени
    workouts.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      return a.time.localeCompare(b.time);
    });

    return workouts;
  };

  const handleAddLocation = async () => {
    if (!newLocationName.trim()) {
      toast.error(t('workout.err.gymNameRequired'));
      return;
    }

    setIsAddingLocation(true);
    try {
      const response = await createTrainerLocation({
        trainer_id: trainerId,
        name: newLocationName.trim(),
        address: newLocationAddress.trim() || undefined,
      });
      
      // API возвращает объект напрямую или обернутый в data
      const locationId = response.data?.id || response.id;
      
      if (!locationId) {
        throw new Error(t('workout.err.createdGymId'));
      }
      
      // Перезагружаем список локаций
      await loadTrainerLocations();
      
      // Автоматически выбираем только что добавленную локацию
      handleFieldChange('location', locationId);
      handleFieldChange('locationType', 'offline');
      
      // Закрываем форму и очищаем поля
      setShowAddLocationForm(false);
      setNewLocationName('');
      setNewLocationAddress('');
      
      toast.success(t('workout.toast.gymAdded'));
    } catch (error) {
      console.error('Ошибка добавления локации:', error);
      
      // Получаем сообщение об ошибке из ответа API
      const errorMessage = error.response?.data?.error || 
                          error.message || 
                          t('workout.err.gymAddFailed');
      
      toast.error(errorMessage);
    } finally {
      setIsAddingLocation(false);
    }
  };


  const loadWorkoutExercises = async () => {
    if (!workout || !workout.id) return;
    
    try {
      const response = await getExercises(workout.id);
      const exercises = Array.isArray(response?.data) ? response.data : [];
      // Преобразуем формат упражнений для формы
      const formattedExercises = exercises.map((ex, index) => ({
        id: ex.id || `exercise-${index}`,
        name: ex.name,
        sets: ex.sets || []
      }));
      setFormData(prev => ({ ...prev, exercises: formattedExercises }));
    } catch (err) {
      console.error('Ошибка загрузки упражнений тренировки:', err);
    }
  };

  const loadWorkoutData = () => {
    if (!workout) return;
    
    const startDate = new Date(`${workout.date}T00:00:00`);
    const startTime = new Date(`${workout.date}T${workout.time}`);
    const endTime = workout.end_time ? new Date(`${workout.date}T${workout.end_time}`) : null;
    const duration = endTime ? Math.round((endTime - startTime) / (1000 * 60)) : 60;
    const isStandardDuration = DURATION_OPTIONS.find(d => d.value === duration);

    setFormData({
      clients: workout.client_id ? [workout.client_id] : [],
      startDate: startDate,
      startTime: startTime,
      duration: isStandardDuration ? duration : 'custom',
      endTime: isStandardDuration ? null : endTime,
      location: workout.location_id || workout.location || '',
      locationType:
        workout.location_type ||
        (workout.location === 'online' || workout.location === API_LOCATION_ONLINE_LABEL
          ? 'online'
          : workout.location_id
          ? 'offline'
          : null),
      recurrenceType: workout.recurrence_type || 'none',
      recurrenceSchedule: [],
      recurrenceEndType: 'weeks',
      recurrenceWeeks: '',
      recurrenceEndDate: null,
      customRecurrenceSettings: workout.custom_recurrence || {
        frequency: 'daily',
        interval: 1,
        intervalType: 'days',
        daysOfWeek: [],
        endType: 'never',
        endCount: 1,
        endDate: null
      },
      programId: workout.program_id || null,
      exercises: workout.exercises || [],
      comment: workout.comment || '',
      planId: workout.plan_id || null
    });
    setSelectedPlanId(workout.plan_id || null);
    setWorkoutPlanId(workout.plan_id || null);
  };

  const resetForm = () => {
    const now = initialDate ? new Date(initialDate) : new Date();
    now.setMinutes(0);
    now.setSeconds(0);
    
    setFormData({
      clients: [],
      title: '',
      startTime: now,
      duration: 60,
      customDuration: '',
      location: '', // Будет хранить 'online' или location_id
    locationType: null, // 'online' или 'offline'
      recurrenceType: 'none',
      recurrenceSchedule: [],
      recurrenceEndType: 'weeks',
      recurrenceWeeks: '',
      recurrenceEndDate: null,
      customRecurrenceSettings: {
        frequency: 'daily',
        interval: 1,
        intervalType: 'days',
        daysOfWeek: [],
        endType: 'never',
        endCount: 1,
        endDate: null
      },
      programId: null,
      exercises: [],
      comment: '',
      planId: null
    });
    setSelectedPlanId(null);
    setHasChanges(false);
    setError('');
    setScheduleConflictError('');
    setValidationErrors({});
    setShowSuccessMessage(false);
    setSuccessMessage('');
    setApplyCommentToAllRecurring(false);
    setPendingBatchWorkouts(null);
    shouldApplyDefaultLocationRef.current = true;
  };

  const calculateEndTime = (startTime, duration, endTime) => {
    if (!startTime) return null;
    if (duration === 'custom' && endTime) {
      return endTime;
    }
    const durationMinutes = duration;
    return new Date(startTime.getTime() + durationMinutes * 60 * 1000);
  };

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.clients || formData.clients.length === 0) {
      errors.clients = t('optForm.errPickClient');
    }

    if (!formData.startDate) {
      errors.startDate = t('optForm.errPickDate');
    }

    if (!formData.startTime) {
      errors.startTime = t('optForm.errPickStartTime');
    }

    if (!formData.duration || (formData.duration === 'custom' && !formData.endTime)) {
      errors.duration = t('optForm.errPickDuration');
    }

    if (formData.recurrenceSchedule.length > 0) {
      const scheduleMap = new Map();
      for (let i = 0; i < formData.recurrenceSchedule.length; i++) {
        const schedule = formData.recurrenceSchedule[i];
        const key = `${schedule.dayOfWeek}-${schedule.time}`;
        if (scheduleMap.has(key)) {
          errors.recurrence = t('optForm.errRecurrenceDuplicateSlot');
          break;
        }
        scheduleMap.set(key, i);
      }

      if (formData.recurrenceEndType === 'weeks') {
        const weeksNum =
          formData.recurrenceWeeks === '' || formData.recurrenceWeeks == null
            ? NaN
            : Number(formData.recurrenceWeeks);
        const hasWeeks = Number.isFinite(weeksNum) && weeksNum >= 1;
        if (!hasWeeks) {
          errors.recurrence = t('optForm.errRecurrenceWeeks');
        }
      } else if (formData.recurrenceEndType === 'date') {
        if (!formData.recurrenceEndDate) {
          errors.recurrence = t('optForm.errRecurrenceEndDate');
        } else if (formData.startDate) {
          const endD = new Date(formData.recurrenceEndDate);
          endD.setHours(0, 0, 0, 0);
          const startD = new Date(formData.startDate);
          startD.setHours(0, 0, 0, 0);
          if (endD < startD) {
            errors.recurrence = t('optForm.errRecurrenceEndBeforeStart');
          }
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSave = async () => {
    if (isLoading) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError('');
    setScheduleConflictError('');

    let workoutData = null;

    try {
      // Объединяем дату и время начала
      const startDate = formData.startDate || new Date();
      let startTime = formData.startTime;
      
      // Если startTime не установлен, используем текущее время
      if (!startTime) {
        const now = new Date();
        startTime = new Date(startDate);
        startTime.setHours(now.getHours(), now.getMinutes(), 0, 0);
      }
      
      const startDateTime = new Date(startDate);
      startDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

      const endTime = calculateEndTime(startDateTime, formData.duration, formData.endTime);
      const endTimeDate = formData.duration === 'custom' && formData.endTime 
        ? new Date(formData.endTime)
        : endTime;
      
      // Убеждаемся, что endTimeDate имеет ту же дату, что и startDate
      if (endTimeDate && formData.startDate) {
        endTimeDate.setFullYear(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      }
      
      // Убеждаемся, что упражнения имеют правильную структуру
      // Подходы и сеты необязательны - если их нет, передаем пустой массив
      // Фильтруем пустые sets (без reps и weight)
      const exercises = (formData.exercises || []).map(ex => {
        const sets = Array.isArray(ex.sets) 
          ? ex.sets.filter(set => {
              // Оставляем только sets с хотя бы одним заполненным полем
              return (set.reps !== undefined && set.reps !== null && set.reps !== '') ||
                     (set.weight !== undefined && set.weight !== null && set.weight !== '');
            })
          : [];
        
        return {
          name: ex.name,
          sets: sets
        };
      });

      // Проверяем, что client_id существует
      if (!formData.clients || formData.clients.length === 0) {
        throw new Error(t('optForm.errNoClientWorkout'));
      }

      workoutData = {
        client_id: formData.clients[0],
        group_clients: formData.clients.slice(1),
        date: (() => {
          const d = startDate;
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        })(),
        time: startTime.toTimeString().slice(0, 5),
        end_time: endTimeDate ? endTimeDate.toTimeString().slice(0, 5) : null,
        comment: formData.comment || null,
        location:
          formData.locationType === 'online'
            ? API_LOCATION_ONLINE_LABEL
            : (formData.locationType === 'offline' && formData.location
              ? trainerLocations.find((l) => l.id === formData.location)?.name
              : null) || null,
        location_type: formData.locationType || (formData.location === 'online' ? 'online' : formData.location && formData.location !== 'online' ? 'offline' : null),
        location_id: formData.locationType === 'offline' && formData.location && formData.location !== 'online' ? formData.location : null,
        recurrence_type: formData.recurrenceType,
        recurrence_days: formData.customRecurrenceSettings.daysOfWeek,
        workout_type: formData.clients.length > 1 ? 'group' : 'individual',
        program_id: formData.programId || null,
        plan_id: formData.planId || selectedPlanId || null,
        exercises: exercises,
        trainer_id: trainerId // Pass trainer_id to link workout to this trainer
      };

      if (formData.recurrenceType === 'custom') {
        workoutData.custom_recurrence = formData.customRecurrenceSettings;
      }

      let response;
      if (isEditMode) {
        response = await updateWorkout(workout.id, workoutData);
      } else {
        // Handle recurring workouts with new schedule-based logic
        if (formData.recurrenceSchedule && formData.recurrenceSchedule.length > 0) {
          const recurringWorkouts = generateRecurringWorkouts(
            startDate,
            formData.recurrenceSchedule,
            formData.recurrenceEndType,
            formData.recurrenceWeeks,
            formData.recurrenceEndDate,
            formData.startTime
          );

          if (recurringWorkouts.length === 0) {
            setError(t('optForm.errBuildSchedule'));
            return;
          }

          const batchPayload = recurringWorkouts.map((workoutSchedule, idx) => {
            const dateStr = (() => {
              const d = workoutSchedule.date;
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            })();

            let endTimeStr = null;
            if (formData.duration === 'custom' && formData.endTime) {
              endTimeStr = new Date(formData.endTime).toTimeString().slice(0, 5);
            } else if (formData.duration !== 'custom') {
              const [hours, minutes] = workoutSchedule.time.split(':').map(Number);
              const et = new Date(workoutSchedule.date);
              et.setHours(hours, minutes, 0, 0);
              et.setMinutes(et.getMinutes() + formData.duration);
              endTimeStr = et.toTimeString().slice(0, 5);
            }

            return {
              ...workoutData,
              date: dateStr,
              time: workoutSchedule.time,
              end_time: endTimeStr,
              comment: !applyCommentToAllRecurring && idx > 0 ? null : workoutData.comment,
              recurrence_type: 'none',
              recurrence_days: null,
              custom_recurrence: null
            };
          });

          try {
            const batchResponse = await createWorkoutsBatch(batchPayload, workoutData.force_merge);
            const created = batchResponse.data?.workouts || [];
            response = {
              data: {
                workouts: created,
                conflicts: false,
                totalCreated: created.length,
                totalPlanned: recurringWorkouts.length
              }
            };
          } catch (error) {
            const batchErr = error.response?.data;
            if (
              error.response?.status === 409 &&
              batchErr?.type === 'time_conflict' &&
              batchErr?.canCreateAnyway &&
              Array.isArray(batchErr.conflicts)
            ) {
              setScheduleConflictError('');
              setError('');
              setPendingBatchWorkouts(batchPayload);
              setPendingTimeConflictWorkoutData(null);
              setTimeConflictDetails({
                conflicts: batchErr.conflicts,
                source: 'batch',
                serverMessage: batchErr.error,
              });
              setShowTimeConflictDialog(true);
              return;
            }
            if (error.response?.status === 409) {
              const lines = Array.isArray(batchErr?.conflicts)
                ? batchErr.conflicts.map((c) => describeBatchTimeConflict(c, t, locale)).filter(Boolean)
                : [];
              setScheduleConflictError(
                lines.length > 0
                  ? `${t('optForm.scheduleConflictIntro')}\n${lines.join('\n')}`
                  : batchErr?.error || t('optForm.timeAlreadyTaken')
              );
              return;
            }
            throw error;
          }
        } else {
          // Single workout
        response = await createWorkout(workoutData);
        if (response?.status === 202 && response?.data?.queued) {
          setShowSuccessMessage(true);
          setSuccessMessage(t('app.offlineSaved'));
          toast.success(t('app.offlineSaved'));
          if (onSave) {
            onSave({ ...workoutData, _queued: true });
          }
          setTimeout(() => {
            onHide();
            resetForm();
          }, 1400);
          return;
        }
        }
      }

      // Set success message based on response
      if (response.data?.workouts) {
        // Серия по расписанию: тот же короткий текст, что и у одной тренировки
        const seriesMsg = t('optForm.successCreated');
        setShowSuccessMessage(true);
        setSuccessMessage(seriesMsg);
        toast.success(seriesMsg);
      } else {
        const oneMsg = isEditMode ? t('optForm.successUpdated') : t('optForm.successCreated');
        setShowSuccessMessage(true);
        setSuccessMessage(oneMsg);
        toast.success(oneMsg);
      }
      
      if (onSave) {
        // Handle both single workout and array of workouts (for recurring)
        const workoutResult = response.data?.workouts || response.data?.workout || response.data;
        onSave(Array.isArray(workoutResult) ? workoutResult : workoutResult);
      }

      setTimeout(() => {
        onHide();
        resetForm();
      }, 2000);

    } catch (err) {
      console.error('Ошибка сохранения тренировки:', err);
      console.error('Детали ошибки:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        workoutData: workoutData
      });
      // Time conflict should be confirmed in a separate AlertDialog (not inline red warning).
      if (
        !isEditMode &&
        err.response?.status === 409 &&
        err.response?.data?.type === 'time_conflict' &&
        err.response?.data?.canCreateAnyway
      ) {
        setPendingBatchWorkouts(null);
        setPendingTimeConflictWorkoutData(workoutData);
        setTimeConflictDetails({
          conflicts: err.response?.data?.conflicts || [],
          source: 'single',
        });
        setError('');
        setShowTimeConflictDialog(true);
        return;
      }

      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        t('optForm.errSaveWorkout', {
          verb: isEditMode ? t('optForm.verbUpdate') : t('optForm.verbCreate'),
        });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmTimeConflictCreate = async () => {
    if (isLoading) {
      return;
    }

    if (pendingBatchWorkouts && pendingBatchWorkouts.length > 0) {
      setIsLoading(true);
      setError('');
      try {
        const batchResponse = await createWorkoutsBatch(pendingBatchWorkouts, true);
        const created = batchResponse.data?.workouts || [];

        setShowTimeConflictDialog(false);
        setPendingBatchWorkouts(null);
        setPendingTimeConflictWorkoutData(null);
        setTimeConflictDetails(null);

        const seriesMsg = t('optForm.successCreated');
        setShowSuccessMessage(true);
        setSuccessMessage(seriesMsg);
        toast.success(seriesMsg);

        if (onSave) {
          onSave(created);
        }

        setTimeout(() => {
          onHide();
          resetForm();
        }, 2000);
      } catch (err) {
        console.error('[OptimizedWorkoutForm] Error force-creating batch:', err);
        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          t('optForm.errCreateBatch');
        setError(errorMessage);
        setShowTimeConflictDialog(false);
        setPendingBatchWorkouts(null);
        setTimeConflictDetails(null);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!pendingTimeConflictWorkoutData) {
      setShowTimeConflictDialog(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await createWorkout({ ...pendingTimeConflictWorkoutData, force_merge: true });
      if (response?.status === 202 && response?.data?.queued) {
        setShowTimeConflictDialog(false);
        setPendingTimeConflictWorkoutData(null);
        setTimeConflictDetails(null);
        setShowSuccessMessage(true);
        setSuccessMessage(t('app.offlineSaved'));
        toast.success(t('app.offlineSaved'));
        if (onSave) {
          onSave({ ...pendingTimeConflictWorkoutData, _queued: true });
        }
        setTimeout(() => {
          onHide();
          resetForm();
        }, 1400);
        return;
      }

      setShowTimeConflictDialog(false);
      setPendingTimeConflictWorkoutData(null);
      setTimeConflictDetails(null);

      setShowSuccessMessage(true);
      setSuccessMessage(t('optForm.successCreated'));
      toast.success(t('optForm.successCreated'));

      if (onSave) {
        const workoutResult = response.data?.workout || response.data;
        onSave(workoutResult);
      }

      setTimeout(() => {
        onHide();
        resetForm();
      }, 2000);
    } catch (err) {
      console.error('[OptimizedWorkoutForm] Error force-creating workout:', err);
      const fd = err.response?.data;
      const errorMessage =
        fd?.error ||
        fd?.message ||
        err.message ||
        t('optForm.errCreateWorkout');
      setError(errorMessage);
      setShowTimeConflictDialog(false);
      setPendingTimeConflictWorkoutData(null);
      setTimeConflictDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!workout) return;

    if (isLoading) {
      return;
    }
    
    if (!window.confirm(t('workout.confirm.deleteWorkoutSimple'))) {
      return;
    }

    setIsLoading(true);
    try {
      await deleteWorkout(workout.id);
      if (onSave) {
        onSave(null);
      }
      onHide();
    } catch (err) {
      console.error('Ошибка удаления тренировки:', err);
      setError(t('workout.err.deleteWorkoutFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = (e) => {
    // Предотвращаем стандартное поведение
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    // Предотвращаем множественные клики
    if (isLoading) {
      return;
    }
    
    if (hasChanges) {
      setShowDiscardDialog(true);
    } else {
      // Просто закрываем модальное окно немедленно
      resetForm();
      onHide();
    }
  };

  const handleProgramSelect = (programId) => {
    if (!programId || programId === 'none') {
      setFormData(prev => ({ ...prev, programId: null, exercises: [] }));
      setHasChanges(true);
      return;
    }

    const program = (Array.isArray(trainingPrograms) ? trainingPrograms : []).find(p => p.id === programId);
    if (program && Array.isArray(program.exercises)) {
      const exercises = program.exercises.map(ex => ({
        name: ex.exercise_name,
        sets: ex.default_sets || [{ reps: '', weight: '' }]
      }));
      // При выборе программы очищаем план
      setFormData(prev => ({ ...prev, programId, exercises, planId: null }));
      setSelectedPlanId(null);
      setHasChanges(true);
    }
    setShowProgramSelector(false);
  };

  const handlePlanSelect = (planId) => {
    if (!planId || planId === 'none') {
      // Очищаем план и упражнения
      setFormData(prev => ({ ...prev, exercises: [], planId: null }));
      setSelectedPlanId(null);
      setHasChanges(true);
      return;
    }

    const plan = (Array.isArray(trainingPrograms) ? trainingPrograms : []).find(p => p.id === planId);
    if (!plan) {
      return;
    }

    // Копируем упражнения из плана в тренировку
    // Подходы и сеты необязательны - если их нет, создаем упражнение без них
    const exercises = (Array.isArray(plan.exercises) ? plan.exercises : []).map(ex => {
      // Получаем sets из default_sets или sets, парсим если это строка
      let sets = ex.default_sets || ex.sets || [];
      
      // Если sets - строка (JSON), парсим её
      if (typeof sets === 'string') {
        try {
          sets = JSON.parse(sets);
        } catch (e) {
          console.error('Ошибка парсинга sets:', e);
          sets = [];
        }
      }
      
      // Если sets пустой массив или не передан, оставляем пустым (необязательное поле)
      if (!Array.isArray(sets) || sets.length === 0) {
        sets = [];
      } else {
        // Убеждаемся, что каждый сет имеет правильную структуру
        sets = sets.map(set => ({
          reps: set.reps !== undefined && set.reps !== null ? set.reps : '',
          weight: set.weight !== undefined && set.weight !== null ? set.weight : ''
        }));
      }

      return {
        id: ex.id || `exercise-${Date.now()}-${Math.random()}`,
        name: ex.exercise_name || ex.name,
        sets: sets // Может быть пустым массивом
      };
    });

    // При выборе плана очищаем программу
    setFormData(prev => ({ ...prev, exercises, planId: plan.id, programId: null }));
    setSelectedPlanId(plan.id);
    setHasChanges(true);
    
    // В режиме редактирования нужно обновить план в тренировке
    if (isEditMode && workout && workout.id) {
      updateWorkout(workout.id, { plan_id: plan.id, program_id: null }).catch(err => {
        console.error('Ошибка обновления plan_id тренировки:', err);
      });
    }
  };

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  }, []);
  
  const handleClientsChange = useCallback((clients) => {
    handleFieldChange('clients', clients);
  }, [handleFieldChange]);

  const selectedProgram = (Array.isArray(trainingPrograms) ? trainingPrograms : []).find(p => p.id === formData.programId);
  const endTime = calculateEndTime(formData.startTime, formData.duration, formData.endTime);

  return (
    <>
      <FullScreenModal 
        open={show} 
        onOpenChange={(open) => {
          // Если модальное окно пытается закрыться
          if (!open) {
            // Если есть изменения и диалог еще не показан - показываем диалог
            if (hasChanges && !showDiscardDialog) {
              setShowDiscardDialog(true);
              // НЕ вызываем onHide, чтобы модальное окно осталось открытым
              return;
            }
            // Если изменений нет или диалог уже был показан - закрываем
            if (!hasChanges) {
              resetForm();
              onHide();
            }
          }
        }}
      >
        <FullScreenModalContent className="flex flex-col">
          {/* Header */}
          <FullScreenModalHeader>
            <FullScreenModalBackButton onClick={handleBack} />
            <FullScreenModalTitle>
              {isEditMode ? t('optForm.titleEdit') : t('optForm.titleCreate')}
            </FullScreenModalTitle>
            <div className="w-10" /> {/* Spacer for centering */}
          </FullScreenModalHeader>

          {/* Body */}
          <FullScreenModalBody>
            <div className="w-full min-w-0 space-y-6 overflow-x-hidden pb-6">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {showSuccessMessage && (
                <Alert className="border-green-500 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    {successMessage || (isEditMode ? t('optForm.successUpdated') : t('optForm.successCreated'))}
                  </AlertDescription>
                </Alert>
              )}

              {/* Клиент */}
              <div className="space-y-2">
                <Label htmlFor="clients" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  {t('optForm.labelClient')}
                </Label>
                <ClientSelectorField
                  value={formData.clients}
                  onValueChange={handleClientsChange}
                  placeholder={t('optForm.phClient')}
                  trainerId={trainerId}
                  singleSelect={true}
                  className={validationErrors.clients ? "border-destructive" : ""}
                />
                {validationErrors.clients && (
                  <p className="text-sm text-destructive">{validationErrors.clients}</p>
                )}
              </div>

              {/* Дата и время начала — одна строка */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2 min-w-0">
                  <Label htmlFor="startDate" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 shrink-0" />
                    <span className="truncate">{t('optForm.labelWorkoutDate')}</span>
                  </Label>
                  <input
                    id="startDate"
                    type="date"
                    value={formData.startDate ? formData.startDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const selectedDate = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                      if (selectedDate) {
                        handleFieldChange('startDate', selectedDate);
                        if (formData.startTime) {
                          const newTime = new Date(formData.startTime);
                          newTime.setFullYear(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
                          handleFieldChange('startTime', newTime);
                        }
                      } else {
                        handleFieldChange('startDate', null);
                      }
                    }}
                    className={`flex h-11 md:h-10 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-base md:text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${validationErrors.startDate ? "border-destructive" : ""}`}
                  />
                  {validationErrors.startDate && (
                    <p className="text-sm text-destructive">{validationErrors.startDate}</p>
                  )}
                </div>

                <div className="space-y-2 min-w-0">
                  <Label htmlFor="startTime" className="flex items-center gap-2">
                    <Clock className="h-4 w-4 shrink-0" />
                    <span className="truncate">{t('optForm.labelStartTime')}</span>
                  </Label>
                  <input
                    id="startTime"
                    type="time"
                    value={formData.startTime ? new Date(formData.startTime).toTimeString().slice(0, 5) : ''}
                    onChange={(e) => {
                      const timeValue = e.target.value;
                      if (timeValue) {
                        const [hours, minutes] = timeValue.split(':').map(Number);
                        if (formData.startDate) {
                          const newTime = new Date(formData.startDate);
                          newTime.setHours(hours, minutes, 0, 0);
                          handleFieldChange('startTime', newTime);
                        } else {
                          const newTime = new Date();
                          newTime.setHours(hours, minutes, 0, 0);
                          handleFieldChange('startTime', newTime);
                        }
                      } else {
                        handleFieldChange('startTime', null);
                      }
                    }}
                    className={`flex h-11 md:h-10 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-base md:text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${validationErrors.startTime ? "border-destructive" : ""}`}
                  />
                  {validationErrors.startTime && (
                    <p className="text-sm text-destructive">{validationErrors.startTime}</p>
                  )}
                </div>
              </div>

                {/* Продолжительность */}
                <div className="space-y-2">
                  <Label htmlFor="duration" className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {t('optForm.labelDuration')}
                  </Label>
                  <select
                    value={formData.duration.toString()}
                    onChange={(e) => {
                      const value = e.target.value;
                      const newDuration = value === 'custom' ? 'custom' : parseInt(value);
                      handleFieldChange('duration', newDuration);

                      if (value === 'custom') {
                        // При выборе "Другое" инициализируем endTime на основе startTime + 60 минут
                        if (formData.startTime && !formData.endTime) {
                          const defaultEndTime = new Date(formData.startTime);
                          defaultEndTime.setMinutes(defaultEndTime.getMinutes() + 60);
                          handleFieldChange('endTime', defaultEndTime);
                        }
                      } else {
                        handleFieldChange('endTime', null);
                      }
                    }}
                    className={`flex h-11 md:h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-base md:text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${validationErrors.duration ? "border-destructive" : ""}`}
                  >
                    <option value="">{t('optForm.durationPlaceholder')}</option>
                      {durationOptions.map(option => (
                      <option key={option.value} value={option.value.toString()}>
                          {option.label}
                      </option>
                      ))}
                  </select>
                  {formData.duration === 'custom' && (
                    <div className="space-y-2">
                      <Label htmlFor="endTime" className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {t('optForm.labelEndTime')}
                      </Label>
                      <input
                        id="endTime"
                        type="time"
                        value={formData.endTime ? new Date(formData.endTime).toTimeString().slice(0, 5) : ''}
                        onChange={(e) => {
                          const timeValue = e.target.value;
                          if (timeValue) {
                            const [hours, minutes] = timeValue.split(':').map(Number);
                          if (formData.startDate) {
                            const newTime = new Date(formData.startDate);
                              newTime.setHours(hours, minutes, 0, 0);
                            handleFieldChange('endTime', newTime);
                          } else {
                              const newTime = new Date();
                              newTime.setHours(hours, minutes, 0, 0);
                              handleFieldChange('endTime', newTime);
                            }
                          } else {
                            handleFieldChange('endTime', null);
                          }
                        }}
                        className={`flex h-11 md:h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-base md:text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${validationErrors.duration ? "border-destructive" : ""}`}
                      />
                    </div>
                  )}
                  {formData.duration !== 'custom' && endTime && formData.startTime && (
                    <p className="text-sm text-muted-foreground">
                      {t('optForm.endsAt', {
                        time: endTime.toLocaleTimeString(getIntlLocaleTag(locale), {
                          hour: '2-digit',
                          minute: '2-digit',
                        }),
                      })}
                    </p>
                  )}
                  {validationErrors.duration && (
                    <p className="text-sm text-destructive">{validationErrors.duration}</p>
                  )}
                </div>

                {/* Повторение - новая логика с днями и временем */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Repeat className="h-4 w-4" />
                      {t('optForm.recurrenceHeading')}
                  </Label>
                    <span className="text-sm text-muted-foreground">
                      {formData.recurrenceSchedule.length > 0
                        ? t('optForm.recurrenceAdded', { n: formData.recurrenceSchedule.length })
                        : t('optForm.recurrenceNoRepeat')}
                    </span>
                  </div>

                  {/* Список добавленных дней и времени */}
                  {formData.recurrenceSchedule.length === 0 ? (
                    <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center">
                      <p className="text-sm text-muted-foreground mb-2">{t('optForm.recurrenceEmpty')}</p>
                      <p className="text-xs text-muted-foreground mb-4">{t('optForm.recurrenceEmptyHint')}</p>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          // Получаем день недели из выбранной даты (0 = воскресенье, 1 = понедельник, и т.д.)
                          let defaultDayOfWeek = 1; // По умолчанию понедельник
                          if (formData.startDate) {
                            defaultDayOfWeek = formData.startDate.getDay();
                          }
                          
                          // Получаем время из выбранного времени начала или используем дефолт
                          let defaultTime = '09:00';
                          if (formData.startTime) {
                            const hours = String(formData.startTime.getHours()).padStart(2, '0');
                            const minutes = String(formData.startTime.getMinutes()).padStart(2, '0');
                            defaultTime = `${hours}:${minutes}`;
                          }
                          
                          // Первое добавление всегда разрешено, так как логично использовать день и время из основных полей
                          // Проверка на дубликат не нужна, так как расписание пустое
                          
                          const newSchedule = [...formData.recurrenceSchedule, { dayOfWeek: defaultDayOfWeek, time: defaultTime }];
                          handleFieldChange('recurrenceSchedule', newSchedule);
                          handleFieldChange('recurrenceType', 'custom');
                        }}
                        className="w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {t('optForm.addDayTime')}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {formData.recurrenceSchedule.map((schedule, index) => {
                        return (
                          <Card key={index} className="border">
                            <CardContent className="p-3">
                              <div className="flex items-end gap-2">
                                <div className="flex-shrink-0 pb-1">
                                  <div className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-semibold">
                                    {index + 1}
                                  </div>
                                </div>
                                
                                <div className="flex-1 grid grid-cols-2 gap-2 min-w-0">
                                  <div className="space-y-1 min-w-0">
                                    <Label className="text-xs text-muted-foreground">{t('optForm.labelWeekday')}</Label>
                                    <select
                                      value={schedule.dayOfWeek}
                                      onChange={(e) => {
                                        const newSchedule = [...formData.recurrenceSchedule];
                                        const newDayOfWeek = parseInt(e.target.value);
                                        const isDuplicate = newSchedule.some((s, i) => 
                                          i !== index && s.dayOfWeek === newDayOfWeek && s.time === schedule.time
                                        );
                                        if (isDuplicate) {
                                          toast.error(t('workout.toast.recurrenceDuplicateSlot'));
                                          return;
                                        }
                                        newSchedule[index].dayOfWeek = newDayOfWeek;
                                        handleFieldChange('recurrenceSchedule', newSchedule);
                                      }}
                                      className="flex h-11 md:h-10 w-full min-w-0 rounded-md border border-input bg-transparent px-2 py-2 text-base md:text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    >
                                      {weekdayShortLabels.map((day, dayIndex) => (
                                        <option key={dayIndex} value={dayIndex}>
                                          {day}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  
                                  <div className="space-y-1 min-w-0">
                                    <Label className="text-xs text-muted-foreground">{t('optForm.labelTimeShort')}</Label>
                                    <input
                                      type="time"
                                      value={schedule.time}
                                      onChange={(e) => {
                                        const newSchedule = [...formData.recurrenceSchedule];
                                        const newTime = e.target.value;
                                        const isDuplicate = newSchedule.some((s, i) => 
                                          i !== index && s.dayOfWeek === schedule.dayOfWeek && s.time === newTime
                                        );
                                        if (isDuplicate) {
                                          toast.error(t('workout.toast.recurrenceDuplicateSlot'));
                                          return;
                                        }
                                        newSchedule[index].time = newTime;
                                        handleFieldChange('recurrenceSchedule', newSchedule);
                                      }}
                                      className="flex h-11 md:h-10 w-full min-w-0 rounded-md border border-input bg-transparent px-2 py-2 text-base md:text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    />
                                  </div>
                                </div>

                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    const newSchedule = formData.recurrenceSchedule.filter((_, i) => i !== index);
                                    handleFieldChange('recurrenceSchedule', newSchedule);
                                    if (newSchedule.length === 0) {
                                      handleFieldChange('recurrenceType', 'none');
                                    }
                                  }}
                                  className="flex-shrink-0 h-11 w-9 text-destructive hover:text-destructive"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          // Получаем день недели из выбранной даты (0 = воскресенье, 1 = понедельник, и т.д.)
                          let defaultDayOfWeek = 1; // По умолчанию понедельник
                          if (formData.startDate) {
                            defaultDayOfWeek = formData.startDate.getDay();
                          }
                          
                          // Получаем время из выбранного времени начала или используем дефолт
                          let defaultTime = '09:00';
                          if (formData.startTime) {
                            const hours = String(formData.startTime.getHours()).padStart(2, '0');
                            const minutes = String(formData.startTime.getMinutes()).padStart(2, '0');
                            defaultTime = `${hours}:${minutes}`;
                          }
                          
                          // Если расписание не пустое, проверяем на дубликат
                          // Если дубликат найден, предлагаем другое значение по умолчанию
                          if (formData.recurrenceSchedule.length > 0) {
                            const isDuplicate = formData.recurrenceSchedule.some(s => 
                              s.dayOfWeek === defaultDayOfWeek && s.time === defaultTime
                            );
                            
                            if (isDuplicate) {
                              // Пытаемся найти свободный день недели с тем же временем
                              let foundAlternative = false;
                              for (let dayOffset = 1; dayOffset < 7; dayOffset++) {
                                const alternativeDay = (defaultDayOfWeek + dayOffset) % 7;
                                const isAlternativeDuplicate = formData.recurrenceSchedule.some(s => 
                                  s.dayOfWeek === alternativeDay && s.time === defaultTime
                                );
                                if (!isAlternativeDuplicate) {
                                  defaultDayOfWeek = alternativeDay;
                                  foundAlternative = true;
                                  break;
                                }
                              }
                              
                              // Если не нашли альтернативный день, пробуем другое время (сдвигаем на час)
                              if (!foundAlternative) {
                                const [hours, minutes] = defaultTime.split(':').map(Number);
                                let newHours = (hours + 1) % 24;
                                defaultTime = `${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                                
                                // Проверяем, не дубликат ли это
                                const isTimeDuplicate = formData.recurrenceSchedule.some(s => 
                                  s.dayOfWeek === defaultDayOfWeek && s.time === defaultTime
                                );
                                if (isTimeDuplicate) {
                                  // Если и это дубликат, просто разрешаем добавление - пользователь может изменить значения
                                  // Это лучше, чем показывать ошибку
                                }
                              }
                            }
                          }
                          
                          const newSchedule = [...formData.recurrenceSchedule, { dayOfWeek: defaultDayOfWeek, time: defaultTime }];
                          handleFieldChange('recurrenceSchedule', newSchedule);
                          handleFieldChange('recurrenceType', 'custom');
                        }}
                        className="w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {t('optForm.addDayTime')}
                      </Button>

                      {scheduleConflictError && (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>{scheduleConflictError}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}

                  {/* Завершение повторения: способ + значение в одной строке */}
                  {formData.recurrenceSchedule.length > 0 && (
                    <Card className={`border ${validationErrors.recurrence ? 'border-destructive' : ''}`}>
                      <CardContent className="p-4 space-y-2">
                        <Label className="text-sm font-medium">{t('optForm.recurrenceEndTitle')}</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="min-w-0">
                            <select
                              id="recurrence-end-type"
                              value={formData.recurrenceEndType}
                              onChange={(e) => {
                                const t = e.target.value;
                                handleFieldChange('recurrenceEndType', t);
                                if (t === 'weeks') {
                                  handleFieldChange('recurrenceEndDate', null);
                                } else {
                                  handleFieldChange('recurrenceWeeks', '');
                                }
                              }}
                              className="flex h-11 md:h-10 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-base md:text-sm text-foreground shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              aria-label={t('optForm.ariaRecurrenceEndMethod')}
                            >
                              <option value="weeks">{t('optForm.recurrenceEndWeeksOpt')}</option>
                              <option value="date">{t('optForm.recurrenceEndDateOpt')}</option>
                            </select>
                          </div>
                          <div className="min-w-0">
                            {formData.recurrenceEndType === 'weeks' ? (
                              <Input
                                id="recurrence-weeks"
                                type="number"
                                min={1}
                                max={52}
                                inputMode="numeric"
                                value={
                                  formData.recurrenceWeeks === '' || formData.recurrenceWeeks == null
                                    ? ''
                                    : formData.recurrenceWeeks
                                }
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === '') {
                                    handleFieldChange('recurrenceWeeks', '');
                                  } else {
                                    const n = parseInt(v, 10);
                                    if (!Number.isNaN(n)) {
                                      handleFieldChange('recurrenceWeeks', n);
                                    }
                                  }
                                }}
                                placeholder={t('optForm.phWeeksCount')}
                                className="h-9 min-w-0 placeholder:text-slate-400"
                                aria-label={t('optForm.ariaRecurrenceWeeks')}
                              />
                            ) : (
                              <input
                                id="recurrence-end-date"
                                type="date"
                                value={
                                  formData.recurrenceEndDate
                                    ? formData.recurrenceEndDate.toISOString().split('T')[0]
                                    : ''
                                }
                                onChange={(e) => {
                                  const date = e.target.value
                                    ? new Date(e.target.value + 'T00:00:00')
                                    : null;
                                  handleFieldChange('recurrenceEndDate', date);
                                }}
                                min={
                                  formData.startDate
                                    ? formData.startDate.toISOString().split('T')[0]
                                    : new Date().toISOString().split('T')[0]
                                }
                                className="flex h-11 md:h-10 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-base md:text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                aria-label={t('optForm.ariaRecurrenceEndDate')}
                              />
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {validationErrors.recurrence && (
                    <p className="text-sm text-destructive">{validationErrors.recurrence}</p>
                  )}
                </div>

              {/* Локация */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {t('optForm.labelLocation')}
                </Label>
                
                {showAddLocationForm ? (
                  // Inline форма для добавления нового зала
                  <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
                    <div className="space-y-2">
                      <Label htmlFor="new-location-name">{t('optForm.labelGymName')}</Label>
                      <Input
                        id="new-location-name"
                        placeholder={t('optForm.phGymName')}
                        value={newLocationName}
                        onChange={(e) => setNewLocationName(e.target.value)}
                        className="w-full placeholder:text-muted-foreground"
                        autoFocus
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-location-address">{t('optForm.labelGymAddress')}</Label>
                      <Input
                        id="new-location-address"
                        placeholder={t('optForm.phGymAddress')}
                        value={newLocationAddress}
                        onChange={(e) => setNewLocationAddress(e.target.value)}
                        className="w-full placeholder:text-muted-foreground"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={handleAddLocation}
                        disabled={!newLocationName.trim() || isAddingLocation}
                        className="flex-1"
                      >
                        {isAddingLocation ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            {t('optForm.saving')}
                          </>
                        ) : (
                          t('optForm.save')
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowAddLocationForm(false);
                          setNewLocationName('');
                          setNewLocationAddress('');
                        }}
                        disabled={isAddingLocation}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Dropdown для выбора локации
                  <select
                    value={formData.location || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'add_new') {
                        setShowAddLocationForm(true);
                      } else if (value === 'online') {
                        handleFieldChange('location', 'online');
                        handleFieldChange('locationType', 'online');
                      } else {
                        handleFieldChange('location', value);
                        handleFieldChange('locationType', 'offline');
                      }
                    }}
                    className={`flex h-11 md:h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-base md:text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${validationErrors.location ? "border-destructive" : ""}`}
                  >
                    <option value="">{t('optForm.selectLocation')}</option>
                    <option value="online">{t('optForm.online')}</option>
                    {trainerLocations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {[location.name, (location.address || '').trim()].filter(Boolean).join(' — ')}
                      </option>
                    ))}
                    <option value="add_new">{t('optForm.addNewGym')}</option>
                  </select>
                )}
                {validationErrors.location && (
                  <p className="text-sm text-destructive">{validationErrors.location}</p>
                )}
              </div>

              {/* Комментарий */}
              <div className="space-y-2">
                <Label htmlFor="comment" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {t('optForm.commentLabel')}
                </Label>
                <Textarea
                  id="comment"
                  value={formData.comment}
                  onChange={(e) => handleFieldChange('comment', e.target.value)}
                  placeholder={t('optForm.commentPlaceholder')}
                  rows={4}
                  className="placeholder:text-slate-400"
                />
                {isRecurringSeries && (
                  <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-3">
                    <Checkbox
                      id="comment-apply-all-recurring"
                      checked={applyCommentToAllRecurring}
                      onCheckedChange={(c) => setApplyCommentToAllRecurring(c === true)}
                      className="mt-0.5"
                      aria-label={t('optForm.commentApplyAria')}
                    />
                    <Label htmlFor="comment-apply-all-recurring" className="cursor-pointer text-sm font-normal leading-snug text-foreground">
                      {t('optForm.commentApplyAll')}
                    </Label>
                  </div>
                )}
              </div>

              <Button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="h-12 w-full text-base font-semibold"
                size="lg"
              >
                {isLoading ? t('optForm.saving') : t('optForm.save')}
              </Button>

              {/* Кнопка Удалить */}
              {isEditMode && (
                <Button
                  type="button"
                  variant="destructive"
                  className="w-full"
                  onClick={handleDelete}
                  disabled={isLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('optForm.deleteWorkout')}
                </Button>
              )}
            </div>
          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>

      {/* Диалог отмены изменений */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('optForm.discardTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('optForm.discardDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDiscardDialog(false)}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowDiscardDialog(false);
                setHasChanges(false);
                resetForm();
                onHide();
              }}
            >
              {t('optForm.discardConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Конфликт времени: подтверждение создания */}
      <AlertDialog
        open={showTimeConflictDialog}
        onOpenChange={(open) => {
          setShowTimeConflictDialog(open);
          if (open) {
            setBatchConflictSlotsExpanded(false);
          }
          if (!open) {
            setPendingTimeConflictWorkoutData(null);
            setPendingBatchWorkouts(null);
            setTimeConflictDetails(null);
            setBatchConflictSlotsExpanded(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('optForm.conflictTitle')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>{t('optForm.conflictAsk')}</p>
                {Array.isArray(timeConflictDetails?.conflicts) && timeConflictDetails.conflicts.length > 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-foreground">
                    <div className="text-xs font-medium text-red-800 mb-2">
                      {timeConflictDetails.source === 'batch'
                        ? t('optForm.conflictBatchHeading')
                        : t('optForm.conflictSingleHeading')}
                    </div>
                    <ul className="space-y-2 list-none m-0 p-0">
                      {timeConflictDetails.source === 'batch'
                        ? (batchConflictSlotsExpanded
                            ? timeConflictDetails.conflicts
                            : timeConflictDetails.conflicts.slice(0, 3)
                          ).map((c, idx) => (
                            <li
                              key={`${c.date}-${c.time}-${idx}`}
                              className="text-xs text-red-800 bg-white rounded p-2 border border-red-200 whitespace-pre-wrap"
                            >
                              {describeBatchTimeConflict(c, t, locale)}
                            </li>
                          ))
                        : timeConflictDetails.conflicts.slice(0, 5).map((w, idx) => (
                            <li key={idx} className="text-xs text-red-700 bg-white rounded p-2 border border-red-200">
                              <div className="font-medium">
                                {w.client_name ||
                                  w.nickname ||
                                  w.client_nickname ||
                                  t('optForm.clientFallback')}
                              </div>
                              <div className="text-gray-600">
                                {t('optForm.conflictWorkoutWhen', {
                                  date: w.date,
                                  time: w.time,
                                  end: w.end_time
                                    ? t('optForm.conflictEndDash', { end: w.end_time })
                                    : '',
                                })}
                              </div>
                            </li>
                          ))}
                    </ul>
                    {timeConflictDetails.source === 'batch' && timeConflictDetails.conflicts.length > 3 && (
                      <button
                        type="button"
                        aria-expanded={batchConflictSlotsExpanded}
                        onClick={() => setBatchConflictSlotsExpanded((v) => !v)}
                        className="mt-2 flex w-full items-center justify-center gap-1 rounded-md border border-red-200 bg-white/90 py-2.5 text-xs font-medium text-red-800 shadow-sm hover:bg-white active:bg-red-50 touch-manipulation"
                      >
                        {batchConflictSlotsExpanded ? (
                          <>
                            {t('optForm.hide')}
                            <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                          </>
                        ) : (
                          <>
                            {t('optForm.showMore', { n: timeConflictDetails.conflicts.length - 3 })}
                            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel disabled={isLoading}>{t('optForm.conflictNo')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmTimeConflictCreate}
              disabled={isLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isLoading ? t('optForm.conflictCreating') : t('optForm.conflictYes')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </>
  );
};

export default OptimizedWorkoutForm;

